import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Weight } from "@faire/web/slate/Font/utils";
import { Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web/ui/LinkAnchor";
import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { Category } from "@faire/retailer/models/Category/Category";
import { assignSettingPruneFeaturedCatnav } from "@faire/retailer/settings/getSettingPruneFeaturedCatnav";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import {
  ICategoryLink,
  useCommunityCollectionsCategoryLinks,
  useRecommendedCategoryLinks,
  useSeasonalCollectionsCategoryLinks,
} from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/LeftNav/__internal__/FeaturedCategoryNavigation/__internal__/featuredCategoryNavigationLinksHelpers";

export const FeaturedCategoryNavigation = ({
  selectedCategory,
}: {
  selectedCategory: Category | undefined;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { categories } = useCategoryStore();
  const communityCollectionsCategoryLinks =
    useCommunityCollectionsCategoryLinks(categories);
  const recommendedCategoryLinks = useRecommendedCategoryLinks(
    categories,
    strictLocalize
  );
  const seasonalCollectionsCategoryLinks =
    useSeasonalCollectionsCategoryLinks(categories);

  const onVisible = useDoActionWhenVisible(() => {
    // assign PRUNE_FEATURED_CATNAV_EXPERIMENT_240515 for logged in control users
    // here, treatment users are assigned through record-category-node-visibility in
    // another UI component
    if (isLoggedInRetailer) {
      assignSettingPruneFeaturedCatnav();
    }
  });

  return (
    <Container data-test-id="category-page-sidebar" ref={onVisible}>
      {seasonalCollectionsCategoryLinks.length > 0 ? (
        <CollectionsCategorySection
          categories={seasonalCollectionsCategoryLinks}
          title={strictLocalize({
            defaultMessage: "Trending Collections",
            description: {
              text: "Seasonal categories of brands related to themes such as self care, women's day, etc",
            },
          })}
          selectedCategory={selectedCategory}
        />
      ) : null}
      {communityCollectionsCategoryLinks.length > 0 ? (
        <CollectionsCategorySection
          categories={communityCollectionsCategoryLinks}
          title={strictLocalize({
            defaultMessage: "Community",
            description: {
              text: "Categories of brands supporting different communities",
            },
          })}
          selectedCategory={selectedCategory}
        />
      ) : null}
      {recommendedCategoryLinks.length > 0 ? (
        <CollectionsCategorySection
          categories={recommendedCategoryLinks}
          title={strictLocalize({
            defaultMessage: "Recommended",
            description: {
              text: "Curated or filtered categories of brands suggested by Faire such as New on Faire, Best Sellers, etc",
            },
          })}
          selectedCategory={selectedCategory}
        />
      ) : null}
    </Container>
  );
};

const Container = styled.nav`
  margin-bottom: 8px;
`;

const CollectionsCategorySectionBase: React.FC<{
  categories: ICategoryLink[];
  title: string;
  selectedCategory?: Category;
}> = ({ categories, title, selectedCategory }) => {
  const listHeaderId = `category-list-header-${title
    .toLowerCase()
    .replace(" ", "-")}`;
  return (
    <>
      <CategoryTitle id={listHeaderId}>{title}</CategoryTitle>
      <Spacer height="1x" />
      <ul aria-labelledby={listHeaderId}>
        {categories.map(({ name, identifier, link }) => {
          const isSelected = selectedCategory?.urlId.id === identifier;
          return (
            <li key={link}>
              <CategoryLink
                onClick={() => trackEvent(`/filter/category/${name}`, true)}
                to={link}
                aria-current={isSelected ? "page" : undefined}
              >
                <CategoryText selected={isSelected}>{name}</CategoryText>
              </CategoryLink>
            </li>
          );
        })}
      </ul>
      <Spacer height="3x" />
    </>
  );
};
const CollectionsCategorySection = observer(CollectionsCategorySectionBase);

const CategoryText = styled(Span)<{
  selected?: boolean;
}>`
  &&& {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    padding-bottom: 2px;
    font-weight: ${(props) => (props.selected ? Weight.medium : Weight.book)};
    border-bottom: ${(props) =>
      props.selected ? css`1px solid ${Core.border.default}` : 0};
  }
`;

const CategoryTitle = styled(Typography).attrs({
  variant: "paragraphSansMedium",
})``;

const CategoryLink = styled(Link)`
  display: block;
  width: 100%;
  cursor: pointer !important;
  font-size: 12px;
  line-height: 32px;
  opacity: 0.82;
  padding: 0;
`;
